import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api, { inr } from "@/lib/api";

/**
 * Build a UPI deep-link string. amount=0 omits the am= field.
 * Format: upi://pay?pa=ID&pn=NAME&am=AMOUNT&cu=INR&tn=Bill-INV
 */
export const buildUpiUri = ({ upi_id, payee_name, amount, note }) => {
  if (!upi_id) return "";
  const p = new URLSearchParams();
  p.set("pa", upi_id);
  if (payee_name) p.set("pn", payee_name);
  if (amount && Number(amount) > 0) p.set("am", Number(amount).toFixed(2));
  p.set("cu", "INR");
  if (note) p.set("tn", note);
  return `upi://pay?${p.toString()}`;
};

export default function Invoice({ order }) {
  const [outlet, setOutlet] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const oid = order?.outlet_id;
    if (!oid) return;
    api
      .get(`/outlets`, { _silent: true })
      .then((r) => {
        if (!cancelled) {
          const o = (r.data || []).find((x) => x.id === oid);
          if (o) setOutlet(o);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [order?.outlet_id]);

  const restName = outlet?.name || "Order Raja";
  const restAddr = outlet?.address || "";
  const restPhone = outlet?.phone || "";
  const restGstin = outlet?.gstin || "";
  const restFssai = outlet?.fssai_no || "";
  const upiId = outlet?.upi_id || "";
  const upiName = outlet?.upi_payee_name || restName;

  const balance = Math.max(0, (order?.total || 0) - (order?.paid_amount || 0));
  const upiAmount = balance > 0 ? balance : order?.total || 0;
  const upiUri = buildUpiUri({
    upi_id: upiId,
    payee_name: upiName,
    amount: upiAmount,
    note: `Bill-${order?.order_no || ""}`,
  });

  return (
    <div className="print-area p-6" data-testid="invoice">
      <div className="text-center border-b border-dashed border-[#EBE8E2] pb-3 mb-3">
        <div className="font-heading font-extrabold text-xl text-[#2D2C2A]">
          {restName}
        </div>
        {restAddr && (
          <div className="text-xs text-[#7A7873] mt-0.5">
            {restAddr}
            {restPhone && ` · ${restPhone}`}
          </div>
        )}
        {!restAddr && restPhone && (
          <div className="text-xs text-[#7A7873] mt-0.5">{restPhone}</div>
        )}
        {restGstin && (
          <div className="text-xs text-[#7A7873]">GSTIN: {restGstin}</div>
        )}
        {restFssai && (
          <div className="text-xs text-[#7A7873]">FSSAI Lic.: {restFssai}</div>
        )}
      </div>

      <div className="text-xs space-y-0.5 mb-3">
        <div className="flex justify-between">
          <span>Invoice</span>
          <span className="font-mono-num">{order.order_no}</span>
        </div>
        {order.token_no && (
          <div className="flex justify-between font-bold text-sm">
            <span>Token</span>
            <span className="font-mono-num text-[#D95D39]">
              {order.token_no}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Date</span>
          <span className="font-mono-num">
            {new Date(order.created_at).toLocaleString("en-IN")}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Type</span>
          <span className="capitalize">{order.type}</span>
        </div>
        {order.waiter_name && (
          <div className="flex justify-between">
            <span>Staff</span>
            <span>{order.waiter_name}</span>
          </div>
        )}
        {order.customer_name && (
          <div className="flex justify-between">
            <span>Customer</span>
            <span>{order.customer_name}</span>
          </div>
        )}
      </div>

      <table className="w-full text-xs mb-3">
        <thead>
          <tr className="border-y border-[#EBE8E2]">
            <th className="text-left py-1.5">Item</th>
            <th className="text-right py-1.5 w-10">Qty</th>
            <th className="text-right py-1.5 w-16">Rate</th>
            <th className="text-right py-1.5 w-20">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(order.items || []).map((it, i) => (
            <tr key={i} className="border-b border-dashed border-[#EBE8E2]">
              <td className="py-1.5">{it.name}</td>
              <td className="py-1.5 text-right font-mono-num">{it.qty}</td>
              <td className="py-1.5 text-right font-mono-num">
                {inr(it.price)}
              </td>
              <td className="py-1.5 text-right font-mono-num">
                {inr(it.price * it.qty)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-xs space-y-0.5 font-mono-num">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>{inr(order.subtotal)}</span>
        </div>
        {order.discount_amount > 0 && (
          <div className="flex justify-between">
            <span>Discount</span>
            <span>− {inr(order.discount_amount)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>CGST</span>
          <span>{inr(order.cgst)}</span>
        </div>
        <div className="flex justify-between">
          <span>SGST</span>
          <span>{inr(order.sgst)}</span>
        </div>
        {order.tip > 0 && (
          <div className="flex justify-between">
            <span>Tip / Gratuity</span>
            <span>{inr(order.tip)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-sm pt-1 mt-1 border-t border-[#2D2C2A] text-[#2D2C2A]">
          <span>TOTAL</span>
          <span>{inr(order.total)}</span>
        </div>
      </div>

      {order.payments?.length > 0 && (
        <div className="text-xs mt-3 pt-3 border-t border-dashed border-[#EBE8E2]">
          <div className="font-semibold mb-1">Payments</div>
          {order.payments.map((p, i) => (
            <div key={i} className="flex justify-between text-[#7A7873]">
              <span className="capitalize">
                {p.mode}
                {p.reference ? ` · ${p.reference}` : ""}
              </span>
              <span className="font-mono-num">{inr(p.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {upiUri && (
        <div className="text-xs mt-3 pt-3 border-t border-dashed border-[#EBE8E2] text-center">
          <div className="font-semibold mb-1.5 text-[#2D2C2A]">
            Scan to Pay (UPI)
          </div>
          <div
            className="inline-block p-2 bg-white border border-[#EBE8E2] rounded-md"
            data-testid="upi-qr"
          >
            <QRCodeSVG
              value={upiUri}
              size={120}
              level="M"
              includeMargin={false}
            />
          </div>
          <div className="text-[10px] text-[#7A7873] mt-1.5 font-mono-num break-all">
            {upiId}
          </div>
          <div className="text-[10px] text-[#7A7873]">
            GPay · PhonePe · Paytm · BHIM
          </div>
        </div>
      )}

      <div className="text-center text-xs text-[#7A7873] mt-4 pt-3 border-t border-dashed border-[#EBE8E2]">
        Thank you for dining with us!
      </div>
    </div>
  );
}
